// @ts-nocheck
// VHP meal planner: interaction layer on top of the Liquid-rendered section (sections/meal-planner.liquid).
// Metafield writes go through the planner API (repo vhp-planner, vhp-planner.vercel.app).
(() => {
  'use strict';

  if (window.__vhpPlannerLoaded) return;
  window.__vhpPlannerLoaded = true;

  const TOKEN_RELOAD_KEY = 'vhp_token_reload';

  const root = document.querySelector('[data-vhp-planner]');
  const configEl = document.querySelector('[data-vhp-planner-config]');
  if (!root || !configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    console.error('[vhp-planner] invalid config', e);
    return;
  }

  const api = (path, body) =>
    fetch(config.apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

  // A logged-in customer needs a token for metafield writes. The API creates it in the customer metafield
  // but never returns it, so reload once and let Liquid print it.
  const ensureToken = async () => {
    if (!config.customerId) return;
    if (config.token) {
      sessionStorage.removeItem(TOKEN_RELOAD_KEY);
      return;
    }
    if (sessionStorage.getItem(TOKEN_RELOAD_KEY)) {
      console.error('[vhp-planner] token creation failed');
      return;
    }
    try {
      await api('/api/token', { customerId: config.customerId });
      sessionStorage.setItem(TOKEN_RELOAD_KEY, '1');
      location.reload();
    } catch (e) {
      console.error('[vhp-planner] token request failed', e);
    }
  };

  // Recipe pools: Liquid renders an empty shell per slot (count + URL of page 1); every page comes from
  // /collections/planner-<slot>?section_id=meal-planner-pool&page=N (sections/meal-planner-pool.liquid).
  const POOL_SECTION_ID = 'meal-planner-pool';

  const num = (value) => (value === '' || value == null ? null : Number(value));

  const readIngredients = (raw) => {
    let list = [];
    try {
      list = Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(list)) return [];
    return list
      .map((row) => {
        if (typeof row === 'string') return { n: row, a: 0, u: '' };
        const n = String(row?.n || '').trim();
        if (!n) return null;
        return { n, a: Number(row.a) || 0, u: String(row.u || '') };
      })
      .filter(Boolean);
  };

  // A pool card carries its planner record as data-recipe (snippets/vhp-planner-recipe.liquid).
  const readCard = (el) => {
    let rec = {};
    try {
      rec = JSON.parse(el.dataset.recipe || '{}') || {};
    } catch (e) {
      rec = {};
    }
    const portions = num(rec.portions);
    return {
      id: String(rec.id || el.dataset.id || ''),
      handle: rec.handle || '',
      title: rec.title || el.querySelector('.card-title')?.textContent.trim() || '',
      image: rec.image || el.querySelector('.card-img img')?.src || '',
      slot: rec.slot,
      kcal: num(rec.kcal),
      protein: num(rec.protein),
      carbs: num(rec.carbs),
      fat: num(rec.fat),
      time: num(rec.time),
      portions: portions > 0 ? portions : 1,
      ingredients: readIngredients(rec.ingredients),
      tags: (Array.isArray(rec.tags) ? rec.tags : []).map((tag) => String(tag).trim()).filter(Boolean),
      exclusive: rec.ex === true,
      el,
    };
  };

  const poolEl = (slot) => root.querySelector(`[data-vhp-planner-pool][data-slot="${slot}"]`);

  const cards = (slot) =>
    Array.from(root.querySelectorAll(`[data-vhp-planner-pool][data-slot="${slot}"] [data-vhp-planner-card]`), readCard);

  const hasMore = (slot) => Boolean(poolEl(slot)?.dataset.nextUrl);

  // Products in the collection / products fetched so far (incl. non-plannable ones that render no card).
  const counts = (slot) => {
    const pool = poolEl(slot);
    return { total: num(pool?.dataset.total) || 0, loaded: num(pool?.dataset.loaded) || 0 };
  };

  const loading = new Map();

  // A request that never answers (weak mobile signal) left "Wird geplant…" or a replace spinning for good: give up
  // after FETCH_TIMEOUT_MS (headers and body), so the caller's retry or error path runs.
  const FETCH_TIMEOUT_MS = 15000;
  const fetchText = async (url) => {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : 0;
    try {
      const response = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.text();
    } finally {
      clearTimeout(timer);
    }
  };

  // Appends the next page of a slot. Resolves to the number of new cards (0 when nothing is left),
  // or null when the request failed. A call while that slot is loading shares the running request,
  // so a caller that arrives during the first-page warm-up below still sees its cards.
  const loadMore = (slot) => {
    const pool = poolEl(slot);
    if (loading.has(slot)) return loading.get(slot);
    if (!pool?.dataset.nextUrl) return Promise.resolve(0);
    const run = (async () => {
      try {
        const url = new URL(pool.dataset.nextUrl, location.origin);
        url.searchParams.set('section_id', POOL_SECTION_ID);
        const doc = new DOMParser().parseFromString(await fetchText(url), 'text/html');
        const incoming = doc.querySelector('[data-vhp-planner-pool]');
        if (!incoming) throw new Error('pool markup missing');

        const known = new Set(cards(slot).map((card) => card.id));
        const list = pool.querySelector('[data-vhp-planner-cards]');
        let added = 0;
        incoming.querySelectorAll('[data-vhp-planner-card]').forEach((el) => {
          if (known.has(el.dataset.id)) return;
          list.append(document.importNode(el, true));
          added += 1;
        });
        // loadAll() may have finished meanwhile; don't reopen a completed pool.
        if (pool.dataset.complete !== '1') {
          pool.dataset.nextUrl = incoming.dataset.nextUrl || '';
          pool.dataset.loaded = incoming.dataset.loaded || pool.dataset.loaded;
        }
        return added;
      } catch (e) {
        console.error('[vhp-planner] loading more recipes failed', slot, e);
        return null;
      }
    })().finally(() => loading.delete(slot));
    loading.set(slot, run);
    return run;
  };

  // Every remaining page of a slot, fetched in parallel (sequential next-page loading took ~10 requests
  // in a row for mains). Cards are appended in page order and deduped. Resolves to the number of new cards.
  // Page size must match the paginate in sections/meal-planner-pool.liquid.
  const PAGE_SIZE = 12;
  const ALL_PARALLEL = 4;
  const loadingAll = new Map();

  const fetchPoolPage = async (url) => {
    const doc = new DOMParser().parseFromString(await fetchText(url), 'text/html');
    const incoming = doc.querySelector('[data-vhp-planner-pool]');
    if (!incoming) throw new Error('pool markup missing');
    return incoming;
  };

  const loadAll = (slot) => {
    const pool = poolEl(slot);
    if (!pool?.dataset.nextUrl && !loading.has(slot)) return Promise.resolve(0);
    if (loadingAll.has(slot)) return loadingAll.get(slot);
    const run = (async () => {
      // Let a running page load (usually the first-page warm-up) land first, so its page isn't fetched twice.
      if (loading.has(slot)) await loading.get(slot);
      if (!pool.dataset.nextUrl) return 0;
      const next = new URL(pool.dataset.nextUrl, location.origin);
      const first = Number(next.searchParams.get('page')) || 2;
      const last = Math.ceil((num(pool.dataset.total) || 0) / PAGE_SIZE);
      const urls = [];
      for (let page = first; page <= last; page += 1) {
        const url = new URL(next);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', POOL_SECTION_ID);
        urls.push(url);
      }
      const pages = new Array(urls.length).fill(null);
      let cursor = 0;
      const worker = async () => {
        while (cursor < urls.length) {
          const index = cursor;
          cursor += 1;
          try {
            pages[index] = await fetchPoolPage(urls[index]);
          } catch (e) {
            console.error('[vhp-planner] loading recipes failed', slot, urls[index].search, e);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(ALL_PARALLEL, urls.length) }, worker));

      const known = new Set(cards(slot).map((card) => card.id));
      const list = pool.querySelector('[data-vhp-planner-cards]');
      let added = 0;
      pages.forEach((incoming) => {
        incoming?.querySelectorAll('[data-vhp-planner-card]').forEach((el) => {
          if (known.has(el.dataset.id)) return;
          known.add(el.dataset.id);
          list.append(document.importNode(el, true));
          added += 1;
        });
      });
      const missing = pages.findIndex((page) => !page);
      if (missing < 0) {
        pool.dataset.complete = '1';
        pool.dataset.nextUrl = '';
        pool.dataset.loaded = pool.dataset.total;
      } else {
        // Keep the first failed page as the next page so loadMore can retry it.
        pool.dataset.nextUrl = urls[missing].pathname + urls[missing].search.replace(/[?&]section_id=[^&]*/, '');
      }
      return added;
    })().finally(() => loadingAll.delete(slot));
    loadingAll.set(slot, run);
    return run;
  };

  // Planner index: every plannable recipe of a slot as JSON (no card markup), for the planner core.
  // /collections/planner-<slot>?section_id=meal-planner-index&page=N (sections/meal-planner-index.liquid).
  // Page 1 tells how many pages there are; the rest load in parallel. A failed page rejects the whole slot
  // (a partial catalog would plan from a biased pool); the next call retries.
  const INDEX_SECTION_ID = 'meal-planner-index';
  const indexCache = new Map();

  const readIndexItem = (row) => {
    if (!row || row.id == null) return null;
    const portions = num(row.portions);
    return {
      id: String(row.id),
      pos: num(row.pos),
      handle: String(row.handle || ''),
      title: String(row.title || ''),
      image: String(row.image || ''),
      slot: String(row.slot || ''),
      kcal: num(row.kcal),
      protein: num(row.protein),
      carbs: num(row.carbs),
      fat: num(row.fat),
      time: num(row.time),
      // Instagram views of the recipe's reel (custom.reel_views; Sörens Woche shows them on his dishes).
      views: num(row.views) || 0,
      portions: portions > 0 ? portions : 1,
      ingredients: readIngredients(row.ingredients),
      tags: Array.isArray(row.tags) ? row.tags.map(String) : [],
      pub: row.pub || null,
    };
  };

  // One retry: a single dropped page would otherwise fail the whole slot (and the plan that waits for it).
  const fetchIndexPage = async (slot, page, retry = 1) => {
    const url = new URL(`/collections/planner-${slot}`, location.origin);
    url.searchParams.set('section_id', INDEX_SECTION_ID);
    url.searchParams.set('page', String(page));
    let text;
    try {
      text = await fetchText(url);
    } catch (e) {
      if (retry > 0) return fetchIndexPage(slot, page, retry - 1);
      throw e;
    }
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const el = doc.querySelector('[data-vhp-planner-index]');
    if (!el) throw new Error('index markup missing');
    return { pages: num(el.dataset.pages) || 1, items: JSON.parse(el.textContent || '[]') };
  };

  const index = (slot) => {
    if (indexCache.has(slot)) return indexCache.get(slot);
    const run = (async () => {
      const first = await fetchIndexPage(slot, 1);
      const rest = await Promise.all(
        Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => fetchIndexPage(slot, i + 2))
      );
      const seen = new Set();
      return [first, ...rest]
        .flatMap((page) => page.items)
        .map(readIndexItem)
        .filter((item) => item && !seen.has(item.id) && seen.add(item.id));
    })();
    indexCache.set(slot, run);
    run.catch((e) => {
      console.error('[vhp-planner] loading the recipe index failed', slot, e);
      indexCache.delete(slot);
    });
    return run;
  };

  // Sörens Woche (docs/SOEREN_WEEK.md): one collection's recipes in their manual order as planner records, `pos` the
  // position in the collection. /collections/<handle>?section_id=meal-planner-week (sections/meal-planner-week.liquid).
  const WEEK_SECTION_ID = 'meal-planner-week';
  const weekCache = new Map();
  const week = (handle) => {
    const key = String(handle || '').trim();
    if (!key) return Promise.resolve([]);
    if (weekCache.has(key)) return weekCache.get(key);
    const run = (async () => {
      const url = new URL(`/collections/${encodeURIComponent(key)}`, location.origin);
      url.searchParams.set('section_id', WEEK_SECTION_ID);
      const text = await fetchText(url);
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const el = doc.querySelector('[data-vhp-planner-week]');
      if (!el) throw new Error('week markup missing');
      const seen = new Set();
      return JSON.parse(el.textContent || '[]')
        .map(readIndexItem)
        .filter((item) => item && !seen.has(item.id) && seen.add(item.id));
    })();
    weekCache.set(key, run);
    run.catch((e) => {
      console.error('[vhp-planner] loading the week collection failed', key, e);
      weekCache.delete(key);
    });
    return run;
  };

  // One specific pool page: the setup deck loads the pages of its next cards (its own order, not page order).
  // Cards are appended and deduped. Resolves to the number of new cards; rejects when the request fails.
  const pageLoads = new Map();
  const loadPage = (slot, page) => {
    const key = `${slot}|${page}`;
    if (pageLoads.has(key)) return pageLoads.get(key);
    const pool = poolEl(slot);
    if (!pool) return Promise.resolve(0);
    const run = (async () => {
      const url = new URL(`/collections/planner-${slot}`, location.origin);
      url.searchParams.set('page', String(page));
      url.searchParams.set('section_id', POOL_SECTION_ID);
      const incoming = await fetchPoolPage(url);
      const known = new Set(cards(slot).map((card) => card.id));
      const list = pool.querySelector('[data-vhp-planner-cards]');
      let added = 0;
      incoming.querySelectorAll('[data-vhp-planner-card]').forEach((el) => {
        if (known.has(el.dataset.id)) return;
        known.add(el.dataset.id);
        list.append(document.importNode(el, true));
        added += 1;
      });
      return added;
    })();
    pageLoads.set(key, run);
    run.catch(() => pageLoads.delete(key));
    return run;
  };

  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-vhp-planner-more]');
    if (!button) return;
    const slot = button.dataset.vhpPlannerMore;
    button.disabled = true;
    await loadMore(slot);
    button.disabled = false;
    if (!hasMore(slot)) button.closest('.load-more-wrap')?.remove();
  });

  const saveProfile = (payload) => {
    if (!config.customerId || !config.token) {
      return Promise.reject(new Error('not signed in'));
    }
    return api('/api/profile', {
      customerId: config.customerId,
      token: config.token,
      ...payload,
    });
  };

  const saveWeek = (payload) => {
    if (!config.customerId || !config.token) {
      return Promise.reject(new Error('not signed in'));
    }
    return api('/api/week', {
      customerId: config.customerId,
      token: config.token,
      ...payload,
    });
  };

  // Custom recipes (paid): { action: list | create | update | autofill | archive, ... }.
  const recipes = (payload) => {
    if (!config.customerId || !config.token) {
      return Promise.reject(new Error('not signed in'));
    }
    return api('/api/recipes', {
      customerId: config.customerId,
      token: config.token,
      ...payload,
    });
  };

  // First page of every pool, fetched right away instead of being part of the page's HTML. Resolves once
  // all of them settled; the funnel waits for it before its first render.
  const poolsReady = Promise.all(
    Array.from(root.querySelectorAll('[data-vhp-planner-pool]'), (pool) =>
      num(pool.dataset.loaded) ? 0 : loadMore(pool.dataset.slot)
    )
  );

  window.vhpPlanner = { root, config, cards, loadMore, loadAll, hasMore, counts, readCard, index, week, loadPage, pageSize: PAGE_SIZE, poolsReady, saveProfile, saveWeek, recipes };

  ensureToken();
})();
